import { useQuery } from '@tanstack/react-query';
import { Building2, Users, Home, AlertTriangle, Headphones } from 'lucide-react';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend, LineChart, Line,
} from 'recharts';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import api from '../lib/api';

interface DashboardData {
    summary: {
        totalTowns: number;
        totalHouseholds: number;
        totalActiveHouseholds: number;
        totalActiveUsers: number;
        expiringTowns: number;
        weeklyCSCount: number;
    };
    onepassAll: { towns: number; households: number; activeHouseholds: number; activeUsers: number };
    onepassNoXi: { towns: number; households: number; activeHouseholds: number; activeUsers: number };
    categoryDistribution: { name: string; count: number; households: number }[];
    topOrderers: { name: string; count: number }[];
    subAppDistribution: { name: string; count: number }[];
    subAppDistributionNoXi: { name: string; count: number }[];
    expiryTimeline: { period: string; count: number }[];
    userGrowth: { month: string; users: number }[];
}

const PIE_COLORS = ['#1ABB9C', '#3498DB', '#9B59B6', '#E74C3C', '#F39C12', '#1ABC9C', '#2ECC71', '#E67E22', '#95A5A6', '#34495E'];

function useDashboard() {
    return useQuery({
        queryKey: ['dashboard'],
        queryFn: async () => {
            const { data } = await api.get<DashboardData>('/dashboard');
            return data;
        },
    });
}

interface ContractChangeLog {
    id: number;
    town_id: number;
    town_name: string;
    old_date: string | null;
    new_date: string | null;
    changed_at: string;
}

function useContractChanges() {
    return useQuery({
        queryKey: ['contract-changes'],
        queryFn: async () => {
            const { data } = await api.get<ContractChangeLog[]>('/dashboard/contract-changes');
            return data;
        },
    });
}

function StatCard({ icon: Icon, label, value, sub, color }: {
    icon: any; label: string; value: string; sub?: string; color: string;
}) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-[#E6E9ED] p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
                <div className="text-2xl font-bold text-[#2A3F54]">{value}</div>
                <div className="text-xs text-[#73879C]">{label}</div>
                {sub && <div className="text-[10px] text-[#ADB5BD] mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-[#E6E9ED] p-5">
            <h3 className="text-sm font-semibold text-[#2A3F54] mb-4">{title}</h3>
            {children}
        </div>
    );
}

/** 도넛 차트 + 오른쪽 항목 테이블 (Gentelella 스타일) */
function DonutWithTable({ data }: { data: { name: string; count: number }[] }) {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    return (
        <div className="flex items-center gap-4">
            <div className="w-[55%] flex-shrink-0">
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            outerRadius={85}
                            innerRadius={45}
                            dataKey="count"
                            stroke="none"
                        >
                            {data.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className="w-[45%]">
                <table className="w-full text-xs" style={{ tableLayout: 'auto' }}>
                    <thead>
                        <tr className="border-b border-[#E6E9ED]">
                            <th className="text-left py-1.5 font-medium text-[#73879C]">항목</th>
                            <th className="text-right py-1.5 font-medium text-[#73879C] pl-4">단지수</th>
                            <th className="text-right py-1.5 font-medium text-[#73879C] pl-3">비율</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, i) => (
                            <tr key={item.name} className="border-b border-[#F5F5F5]">
                                <td className="py-1.5">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                        <span className="text-[#2A3F54]">{item.name}</span>
                                    </span>
                                </td>
                                <td className="text-right py-1.5 text-[#2A3F54] font-medium pl-4">{item.count}</td>
                                <td className="text-right py-1.5 text-[#73879C] pl-3">{total > 0 ? Math.round((item.count / total) * 100) : 0}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border border-[#E6E9ED] rounded shadow-lg px-3 py-2 text-xs">
            <div className="font-medium text-[#2A3F54]">{label || payload[0]?.name}</div>
            {payload.map((p: any, i: number) => (
                <div key={i} className="text-[#73879C]">
                    {p.dataKey === 'count' ? '단지 수' : p.name}: <span className="font-semibold">{p.value?.toLocaleString()}</span>
                </div>
            ))}
        </div>
    );
};

const RADIAN = Math.PI / 180;
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
    if (percent < 0.04) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

export default function DashboardPage() {
    const { data, isLoading } = useDashboard();
    const { data: contractChanges } = useContractChanges();

    if (isLoading || !data) {
        return (
            <Layout>
                <Header title="대시보드" subtitle="단지 관리 현황을 한눈에 확인합니다" />
                <div className="px-8 py-6 flex items-center justify-center h-64">
                    <div className="text-sm text-gray-400">불러오는 중...</div>
                </div>
            </Layout>
        );
    }

    const { summary, categoryDistribution, topOrderers, subAppDistribution, subAppDistributionNoXi, expiryTimeline } = data;
    const { onepassAll, onepassNoXi } = data;

    return (
        <Layout>
            <Header title="대시보드" subtitle="단지 관리 현황을 한눈에 확인합니다" />

            <div className="px-8 py-6 space-y-6">
                {/* 상단 요약 카드 */}
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    <StatCard
                        icon={Building2}
                        label="전체 단지"
                        value={summary.totalTowns.toLocaleString()}
                        color="bg-[#1ABB9C]"
                    />
                    <StatCard
                        icon={Home}
                        label="세대수"
                        value={summary.totalHouseholds.toLocaleString()}
                        color="bg-[#3498DB]"
                    />
                    <StatCard
                        icon={Home}
                        label="사용 세대"
                        value={summary.totalActiveHouseholds.toLocaleString()}
                        color="bg-[#2ECC71]"
                    />
                    <StatCard
                        icon={Users}
                        label="사용자"
                        value={summary.totalActiveUsers.toLocaleString()}
                        color="bg-[#9B59B6]"
                    />
                    <StatCard
                        icon={AlertTriangle}
                        label="만료 예정"
                        value={summary.expiringTowns.toLocaleString()}
                        sub="60일 이내"
                        color="bg-[#E74C3C]"
                    />
                    <StatCard
                        icon={Headphones}
                        label="CS 접수"
                        value={summary.weeklyCSCount.toLocaleString()}
                        sub="이번 주"
                        color="bg-[#F39C12]"
                    />
                </div>

                {/* 원패스 서비스 단지 */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg shadow-sm border border-[#E6E9ED] p-4">
                        <h4 className="text-[11px] font-semibold text-[#73879C] uppercase tracking-wider mb-3">원패스 서비스 단지 (전체)</h4>
                        <div className="grid grid-cols-4 gap-3 text-center">
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassAll.towns.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">단지</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassAll.households.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">세대수</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassAll.activeHouseholds.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">사용세대</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassAll.activeUsers.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">사용자</p></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-[#E6E9ED] p-4">
                        <h4 className="text-[11px] font-semibold text-[#73879C] uppercase tracking-wider mb-3">원패스 서비스 단지 (자이 제외)</h4>
                        <div className="grid grid-cols-4 gap-3 text-center">
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassNoXi.towns.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">단지</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassNoXi.households.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">세대수</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassNoXi.activeHouseholds.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">사용세대</p></div>
                            <div><p className="text-xl font-bold text-[#2A3F54]">{onepassNoXi.activeUsers.toLocaleString()}</p><p className="text-[10px] text-[#ADB5BD]">사용자</p></div>
                        </div>
                    </div>
                </div>

                {/* 차트 영역 - 2열 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 구분별 현황 */}
                    <ChartCard title="구분별 단지 현황">
                        <DonutWithTable data={categoryDistribution} />
                    </ChartCard>

                    {/* 사용 어플 분포 — 전체 */}
                    <ChartCard title="사용 어플 분포 (전체)">
                        <DonutWithTable data={subAppDistribution} />
                    </ChartCard>

                    {/* 사용 어플 분포 — 자이패스 제외 */}
                    <ChartCard title="사용 어플 분포 (자이패스 제외)">
                        <DonutWithTable data={subAppDistributionNoXi} />
                    </ChartCard>

                    {/* 발주처 TOP 10 */}
                    <ChartCard title="발주처 TOP 10">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={topOrderers} layout="vertical" margin={{ left: 20, right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9ED" />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#73879C' }} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={100}
                                    tick={{ fontSize: 11, fill: '#2A3F54' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" fill="#3498DB" radius={[0, 4, 4, 0]} barSize={20} name="단지 수" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* 계약 만료 타임라인 */}
                    <ChartCard title="계약 만료 타임라인">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={expiryTimeline} margin={{ left: 0, right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9ED" />
                                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#73879C' }} />
                                <YAxis tick={{ fontSize: 11, fill: '#73879C' }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={40} name="단지 수">
                                    {expiryTimeline.map((entry, i) => (
                                        <Cell
                                            key={i}
                                            fill={i === 0 ? '#E74C3C' : i === 1 ? '#F39C12' : i === 2 ? '#F1C40F' : '#3498DB'}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* 사용자 증가 추이 (전체 너비) */}
                <ChartCard title="원패스 사용자 증가 추이 (자이 제외)">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data.userGrowth} margin={{ left: 20, right: 20, top: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9ED" />
                            <XAxis
                                dataKey="month"
                                tick={{ fontSize: 10, fill: '#73879C' }}
                                interval={5}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: '#73879C' }}
                                tickFormatter={(v: number) => `${Math.round(v / 1000)}K`}
                            />
                            <Tooltip
                                formatter={(value: number) => [value.toLocaleString() + '명', '사용자']}
                                labelFormatter={(label: string) => `20${label}`}
                            />
                            <Line
                                type="monotone"
                                dataKey="users"
                                stroke="#1ABB9C"
                                strokeWidth={2}
                                dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const isJan = payload.month.endsWith('.01');
                                    const isLast = props.index === data.userGrowth.length - 1;
                                    if (!isJan && !isLast) return <circle key={props.index} cx={0} cy={0} r={0} />;
                                    return <circle key={props.index} cx={cx} cy={cy} r={4} fill="#1ABB9C" stroke="white" strokeWidth={2} />;
                                }}
                                label={(props: any) => {
                                    const { x, y, value, index } = props;
                                    const month = data.userGrowth[index]?.month;
                                    const isJan = month?.endsWith('.01');
                                    const isLast = index === data.userGrowth.length - 1;
                                    if (!isJan && !isLast) return null;
                                    return (
                                        <text x={x} y={y - 10} textAnchor="middle" fontSize={10} fill="#2A3F54" fontWeight={600}>
                                            {Math.round(value / 1000)}K
                                        </text>
                                    );
                                }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                {/* 계약종료일 변경 로그 */}
                {contractChanges && contractChanges.length > 0 && (
                    <ChartCard title="계약종료일 변경 내역 (최근 30일)">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-[#E6E9ED]">
                                        <th className="text-left py-2 font-medium text-[#73879C]">단지명</th>
                                        <th className="text-center py-2 font-medium text-[#73879C]">이전 종료일</th>
                                        <th className="text-center py-2 font-medium text-[#73879C]">변경 종료일</th>
                                        <th className="text-right py-2 font-medium text-[#73879C]">변경일시</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contractChanges.map((log) => (
                                        <tr key={log.id} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB]">
                                            <td className="py-2 text-[#2A3F54] font-medium">{log.town_name}</td>
                                            <td className="py-2 text-center text-[#E74C3C]">{log.old_date || '-'}</td>
                                            <td className="py-2 text-center text-[#1ABB9C] font-medium">{log.new_date || '-'}</td>
                                            <td className="py-2 text-right text-[#ADB5BD]">{new Date(log.changed_at).toLocaleString('ko-KR')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ChartCard>
                )}
            </div>
        </Layout>
    );
}
